import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Loader2, Download } from "lucide-react";
import { Logo } from "@/components/Logo";
import { getPaymentStatus, API } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let active = true;

    const poll = async () => {
      if (!active) return;
      try {
        const data = await getPaymentStatus(sessionId);
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("paid");
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
        if (data.status === "expired" || data.status === "failed") { setState("error"); return; }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) { setState("pending"); return; }
        setTimeout(poll, POLL_INTERVAL);
      } catch {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) { setState("error"); return; }
        setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, clearCart]);

  return (
    <main className="min-h-screen bg-emerald text-cream flex items-center justify-center px-6 py-32" data-testid="payment-success-page">
      <div className="max-w-lg w-full text-center">
        {state === "checking" && (
          <div className="flex flex-col items-center gap-6" data-testid="payment-checking">
            <Loader2 className="w-12 h-12 text-gold animate-spin" />
            <p className="font-editorial italic text-3xl">Confirming your order…</p>
          </div>
        )}

        {state === "paid" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} data-testid="payment-paid">
            <motion.div
              initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-20 h-20 bg-gold rounded-full flex items-center justify-center mx-auto mb-8"
            >
              <Check className="w-10 h-10 text-emerald" strokeWidth={3} />
            </motion.div>
            <p className="eyebrow text-gold mb-4">Order Confirmed</p>
            <h1 className="font-display text-4xl md:text-5xl leading-tight mb-6">Thank you.</h1>
            <p className="font-body text-cream/80 text-lg leading-relaxed mb-8">
              Your order is confirmed. A receipt is on its way to your inbox — and with it, your place in a growing movement of love that multiplies.
            </p>
            {order?.items?.length > 0 && (
              <div className="bg-emerald-deep border border-cream/15 p-6 text-left mb-8">
                {order.items.map((it, i) => {
                  const price = Number(it.price);
                  const lineTotal = Number.isFinite(price) ? (price * it.quantity).toFixed(2) : "—";
                  return (
                    <div key={i} className="flex justify-between py-2 font-body text-sm border-b border-cream/10 last:border-0">
                      <span className="text-cream/80">{it.name} × {it.quantity}</span>
                      <span className="text-gold">{Number.isFinite(price) ? `$${lineTotal}` : "—"}</span>
                    </div>
                  );
                })}
                {order.amount != null && (
                  <div className="flex justify-between pt-4 font-body font-semibold">
                    <span>Total</span><span className="text-gold">${order.amount.toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}

            {order?.items?.some((it) => (it.lookup_key || "").startsWith("pr_")) && (
              <div className="border border-gold/40 bg-gold/10 p-6 mb-8 space-y-3" data-testid="printable-delivery">
                <p className="eyebrow text-gold flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" /> Your Printable Downloads
                </p>
                <p className="font-body text-cream/80 text-sm">
                  Your PDFs are ready. Download them now — the links stay tied to this order confirmation.
                </p>
                {order.items
                  .filter((it) => (it.lookup_key || "").startsWith("pr_"))
                  .map((it) => (
                    <a
                      key={it.lookup_key}
                      href={`${API}/download/printable/${it.lookup_key.replace(/^pr_/, "")}?session_id=${sessionId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-gold w-full justify-center"
                      data-testid={`download-${it.lookup_key}`}
                    >
                      <Download className="w-4 h-4" /> {it.name} (PDF)
                    </a>
                  ))}
              </div>
            )}

            {order?.items?.some((it) => (it.lookup_key || "").startsWith("gift:")) && (
              <div className="border border-gold/40 bg-gold/10 p-6 mb-8" data-testid="gift-delivery">
                <p className="eyebrow text-gold mb-3 flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" /> Your Gift Certificate
                </p>
                <p className="font-body text-cream/80 text-sm mb-5">
                  Download the printable gift certificate — a keepsake to hand-deliver or attach to a card.
                  If you chose email delivery, the recipient has also been sent their claim link.
                </p>
                <a
                  href={`${API}/academy/gift/certificate/${sessionId}`}
                  className="btn-gold w-full justify-center"
                  data-testid="gift-certificate-btn"
                >
                  <Download className="w-4 h-4" /> Download Gift Certificate (PDF)
                </a>
              </div>
            )}
            {order?.items?.some((it) => it.lookup_key === "lg_ebook") && (
              <div className="border border-gold/40 bg-gold/10 p-6 mb-8" data-testid="ebook-delivery">
                <p className="eyebrow text-gold mb-3 flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" /> Your Digital Edition
                </p>
                <p className="font-body text-cream/80 text-sm mb-5">
                  Instant access — your secure download link is ready.
                </p>
                <a
                  href={`${API}/download/${sessionId}?format=pdf`}
                  className="btn-gold w-full justify-center"
                  data-testid="ebook-download-btn"
                >
                  <Download className="w-4 h-4" /> Download PDF
                </a>
                <a
                  href={`${API}/download/${sessionId}?format=epub`}
                  className="btn-outline w-full justify-center mt-3 border-gold/40 text-cream hover:bg-gold hover:text-ink hover:border-gold"
                  data-testid="ebook-download-epub-btn"
                >
                  <Download className="w-4 h-4" /> Download EPUB (Kindle & phones)
                </a>
              </div>
            )}
            <Link to="/" className="btn-gold" data-testid="success-home-btn">
              Return Home <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        )}

        {state === "pending" && (
          <div className="flex flex-col items-center gap-6" data-testid="payment-pending">
            <Logo className="w-16 h-16 text-gold" stroke="#D4AF37" />
            <p className="font-editorial italic text-3xl">Your payment is processing.</p>
            <p className="font-body text-cream/70">We’ll email your confirmation as soon as it settles. You can safely close this page.</p>
            <Link to="/" className="btn-gold mt-2">Return Home</Link>
          </div>
        )}

        {state === "error" && (
          <div className="flex flex-col items-center gap-6" data-testid="payment-error">
            <Logo className="w-16 h-16 text-gold" stroke="#D4AF37" />
            <p className="font-editorial italic text-3xl">We couldn’t confirm this order.</p>
            <p className="font-body text-cream/70">If you completed payment, your receipt will still arrive by email. Otherwise, please try again.</p>
            <Link to="/store" className="btn-gold mt-2">Back to Store</Link>
          </div>
        )}
      </div>
    </main>
  );
};

export default PaymentSuccess;
